import React, { useMemo, useState, useEffect, useCallback } from "react";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Fan, FanServices, FanSourceSystem, fanSourceNames, PlayersServices } from "@/services/bedrock";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DropdownMenu,
  confirmDialog,
  Input,
  Combobox,
  PlayerCell,
  ContactCell,
  Spinner,
} from "@/components/ui";
import { Plus, Users, MoreHorizontal, Pencil, Trash, Search, Link2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { FanDialog, FanActivityDialog, FanFormRequestDialog } from "./components";
import { ShareFormLinkDialog } from "@/components/ShareFormLinkDialog";
import { usePaginatedCombobox } from "@/utils";
import { fanActivityLabel } from "./fanActivityLabel";

const SOURCES = (Object.keys(fanSourceNames) as FanSourceSystem[]).map(key => ({
  id: key,
  label: fanSourceNames[key],
}));

export interface FansProps {
  isPlayerContext?: boolean;
  playerId?: number;
}

export function Fans({ isPlayerContext = false, playerId }: FansProps = {}) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [accumulatedFans, setAccumulatedFans] = useState<Fan[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedFan, setSelectedFan] = useState<Fan | null>(null);

  const handleActivityClick = useCallback((fan: Fan) => {
    setSelectedFan(fan);
    setActivityDialogOpen(true);
  }, []);

  const handleRequestFeedbackClick = useCallback((fan: Fan) => {
    setSelectedFan(fan);
    setRequestDialogOpen(true);
  }, []);

  // Search & Filter States
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSource, setSelectedSource] = useState<{ id: string; label: string } | null>(null);

  // Debounce search input to prevent hammering the API
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchInput]);

  // Step 1: get players list for filter (Only needed for Coach context)
  const getPlayers = useCallback(async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
      })),
    };
  }, []);

  const players = usePaginatedCombobox(getPlayers, false, null);

  const activePlayerId = isPlayerContext ? playerId : players.value?.id;

  useEffect(() => {
    setPage(1);
  }, [activePlayerId, selectedSource?.id]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["fans", page, debouncedSearch, activePlayerId, selectedSource?.id],
    queryFn: () =>
      FanServices.getFans({
        page,
        perPage: 25,
        search: debouncedSearch || undefined,
        playerId: activePlayerId,
        sourceSystem: selectedSource?.id as any,
        active: true,
        extraFields: ["latest_activity", "player"],
      }),
  });

  const saveMutation = useMutation({
    mutationFn: async (formData: any) => {
      const payload = {
        ...formData,
        playerId: isPlayerContext ? playerId : formData.playerId,
      };
      if (selectedFan) {
        return FanServices.updateFan(selectedFan.id, payload);
      } else {
        return FanServices.createFan(payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fans"] });
      setDialogOpen(false);
      setSelectedFan(null);
    },
    onError: err => {
      console.error("Failed to save fan", err);
      alert("Error saving contact. Please try again.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string | number) => {
      return FanServices.deleteFan(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fans"] });
    },
    onError: err => {
      console.error("Failed to delete fan", err);
      alert("Error deleting contact. Please try again.");
    },
  });

  const handleEditClick = (fan: Fan) => {
    setSelectedFan(fan);
    setDialogOpen(true);
  };

  const handleDeleteClick = async (fan: Fan) => {
    const confirmed = await confirmDialog({
      title: "Delete Fan",
      description: (
        <span>
          Are you sure you want to delete <strong>{fan.displayName}</strong>? This action cannot be undone.
        </span>
      ),
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (confirmed) {
      deleteMutation.mutate(fan.id);
    }
  };

  const handleFormSubmit = (formData: any) => {
    saveMutation.mutate(formData);
  };

  useEffect(() => {
    if (!isFetching && data?.content) {
      if (data.pagination?.page === page) {
        if (page === 1) {
          setAccumulatedFans(data.content);
        } else {
          setAccumulatedFans(prev => {
            const newFans = data.content.filter((f: any) => !prev.some((existing: any) => existing.id === f.id));
            return [...prev, ...newFans];
          });
        }
      }
    }
  }, [data, page, isFetching]);

  const hasNextPage = page < (data?.pagination?.totalPages || 1);

  const infiniteScrollProps = useMemo(() => {
    return {
      hasNextPage,
      isFetchingNextPage: isFetching,
      fetchNextPage: () => {
        if (hasNextPage && !isFetching) {
          setPage(prev => prev + 1);
        }
      },
    };
  }, [hasNextPage, isFetching]);

  const columns = useMemo<ColumnDef<Fan>[]>(() => {
    const cols: ColumnDef<Fan>[] = [
      {
        id: "contactInfo",
        header: "Contact",
        cell: ({ row }) => {
          const fan = row.original;
          return (
            <ContactCell
              name={fan.displayName}
              email={fan.email}
              phoneNumber={fan.phoneNumber}
              onClick={() => handleEditClick(fan)}
            />
          );
        },
      },
    ];

    if (!isPlayerContext) {
      cols.push({
        id: "assignedPlayer",
        header: "Assigned Player",
        cell: ({ row }) => {
          const player = row.original.player;
          if (!player) {
            return <span className="text-slate-400 italic text-sm">Unassigned</span>;
          }
          return <PlayerCell player={player} />;
        },
      });
    }

    cols.push(
      {
        id: "source",
        header: "Source",
        cell: ({ row }) => {
          const fan = row.original;
          const source = fan.sourceSystem;
          const readableName = fanSourceNames[source] || source;
          return (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-5 font-sans select-none">
              {readableName}
            </Badge>
          );
        },
      },
      {
        id: "createdAt",
        header: "Created",
        cell: ({ row }) => {
          const fan = row.original;
          const dateStr = fan.createdAt;
          let formattedDate = "-";
          if (dateStr) {
            try {
              const date = new Date(dateStr);
              formattedDate = date.toLocaleDateString();
            } catch {
              formattedDate = dateStr;
            }
          }
          return <span className="text-slate-500 font-sans text-xs select-none">{formattedDate}</span>;
        },
      },
      {
        id: "latestActivity",
        header: "Latest Activity",
        cell: ({ row }) => {
          const fan = row.original;
          const activity = fan.latestActivity;
          if (!activity) {
            return <span className="text-slate-400 italic text-xs font-sans">No activity recorded</span>;
          }

          const readableType = fanActivityLabel(activity);
          const dateStr = activity.occurredAt || activity.createdAt;
          let formattedDate = "";
          if (dateStr) {
            try {
              const d = new Date(dateStr);
              formattedDate = ` on ${d.toLocaleDateString()}`;
            } catch {}
          }

          return (
            <div
              className="flex flex-col gap-0.5 text-xs font-sans cursor-pointer hover:text-primary group/activity select-none"
              onClick={e => {
                e.stopPropagation();
                handleActivityClick(fan);
              }}
            >
              <span className="font-semibold text-slate-700 group-hover/activity:text-primary group-hover/activity:underline">
                {readableType}
              </span>
              {formattedDate && <span className="text-slate-400 text-[10px]">{formattedDate.trim()}</span>}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const fan = row.original;
          return (
            <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenu.Trigger
                  render={props => (
                    <Button
                      {...props}
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-slate-800"
                    >
                      <span className="sr-only">Actions</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  )}
                />
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Item onClick={() => handleRequestFeedbackClick(fan)}>
                    <Link2 className="mr-2 h-4 w-4" />
                    <span>Request Feedback</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onClick={() => handleEditClick(fan)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    <span>Edit Contact Info</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onClick={() => handleDeleteClick(fan)}>
                    <Trash className="mr-2 h-4 w-4" />
                    <span>Delete Fan</span>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </div>
          );
        },
      }
    );

    return cols;
  }, [isPlayerContext, handleActivityClick, handleRequestFeedbackClick]);

  const isEmpty =
    !isLoading &&
    data !== undefined &&
    data.pagination?.totalCount === 0 &&
    !debouncedSearch &&
    !selectedSource?.id &&
    (isPlayerContext ? true : !players.value?.id);

  return (
    <div className="flex flex-col gap-4 mt-4 flex-1 min-h-0 h-full">
      {isEmpty ? (
        <FansEmptyState
          onCreateClick={() => {
            setSelectedFan(null);
            setDialogOpen(true);
          }}
        />
      ) : (
        <>
          {/* Top Filters & Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 select-none shrink-0">
            {/* Search Input */}
            <div className="relative w-full sm:w-64 shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search contacts..."
                className="bg-white pl-9 text-xs"
              />
            </div>

            {/* Player Filter (Only in Coach context) */}
            {!isPlayerContext && (
              <Combobox
                {...players}
                showClear
                placeholder="Filter by Player..."
                className="w-full sm:w-64 bg-white shrink-0"
              />
            )}

            {/* Source Filter */}
            <Combobox
              items={SOURCES}
              value={selectedSource}
              onValueChange={setSelectedSource}
              showClear
              placeholder="Filter by Source..."
              className="w-full sm:w-48 bg-white shrink-0"
            />

            {/* Create Fan button (pushed to far right on sm+ screens) */}
            <Button
              onClick={() => {
                setSelectedFan(null);
                setDialogOpen(true);
              }}
              disabled={isLoading}
              variant="outline"
              className="shrink-0 sm:ml-auto w-full sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Fan
            </Button>
          </div>

          <div className="flex flex-col flex-1 min-h-0 h-full bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <DataTable
              columns={columns}
              data={accumulatedFans}
              rowCount={accumulatedFans.length}
              loading={isFetching && accumulatedFans.length === 0}
              scrollable={true}
              infiniteScroll={infiniteScrollProps}
            />

            {isFetching && page > 1 && (
              <div className="flex items-center justify-center gap-2 py-3 bg-slate-50/50 border-t border-slate-100 shrink-0 animate-in fade-in slide-in-from-bottom-1 duration-200">
                <Spinner className="size-3.5 text-slate-500 animate-spin" />
                <span className="text-xs font-semibold text-slate-500">Loading more contacts...</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Create / Edit Dialog */}
      <FanDialog
        open={dialogOpen}
        onOpenChange={open => {
          setDialogOpen(open);
          if (!open) {
            setSelectedFan(null);
          }
        }}
        selectedFan={selectedFan}
        onFormSubmit={handleFormSubmit}
        isSubmitting={saveMutation.isPending}
        isPlayerContext={isPlayerContext}
      />

      {/* Fan Activity History Dialog */}
      <FanActivityDialog
        open={activityDialogOpen}
        onOpenChange={open => {
          setActivityDialogOpen(open);
          if (!open) {
            setSelectedFan(null);
          }
        }}
        fan={selectedFan}
        onFindFans={email => {
          setActivityDialogOpen(false);
          setSelectedFan(null);
          setSearchInput(email);
        }}
      />

      {/* Create Form Link Dialog */}
      {!isPlayerContext ? (
        <FanFormRequestDialog
          open={requestDialogOpen}
          onOpenChange={open => {
            setRequestDialogOpen(open);
            if (!open) {
              setSelectedFan(null);
            }
          }}
          fan={selectedFan}
        />
      ) : (
        <ShareFormLinkDialog
          open={requestDialogOpen}
          onOpenChange={open => {
            setRequestDialogOpen(open);
            if (!open) {
              setSelectedFan(null);
            }
          }}
          playerId={playerId!}
          initialFan={selectedFan || undefined}
          initialWizardStep={2}
          initialShowCustom={true}
        />
      )}
    </div>
  );
}

function FansEmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-12 text-center bg-card shadow-sm mt-8">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6 text-primary">
        <Users className="h-10 w-10" />
      </div>
      <h3 className="text-2xl font-bold tracking-tight mb-2">Build Your Fan Base</h3>
      <p className="text-muted-foreground max-w-md mx-auto mb-8">
        Manage your client contacts, track survey engagement, and monitor social activity. The Fan Zone is your central
        hub for building and maintaining client relationships.
      </p>
      <Button size="lg" onClick={onCreateClick}>
        <Plus className="mr-2 h-5 w-5" />
        Create your first fan
      </Button>
    </div>
  );
}
